import React from "react";
import { motion } from "framer-motion";
import {
  Clock,
  ArrowRight,
  BadgeCheck,
  Headphones,
  Calendar,
  Award,
  Rocket,
  Users,
  Zap,
  Globe,
  HandHeart,
} from "lucide-react";
import {
  downAndUp,
  scrollVariantsLeft,
  scrollVariantsRight,
  upAndDown,
} from "../../../../constants/TextAnimation";
import StaggeredText from "./StaggeredText";
import { useNavigate, Link } from "react-router-dom";

const TimelineItem = ({ year, title, description, icon, position }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      variants={scrollVariantsLeft}
      viewport={{ amount: 0.3 }}
      className={`flex items-center justify-between mb-8 cursor-pointer ${
        position === "right" ? "flex-row-reverse" : ""
      }`}
    >
      <div
        className={`w-12/12 md:w-5/12 hover:bg-slate-900/50 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg rounded-xl p-3 ${
          position === "right" ? "text-right" : ""
        }`}
      >
        <div className="mb-2">
          <span className="inline-block px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm font-medium">
            {year}
          </span>
        </div>
        <h3 className="text-lg lg:text-xl font-semibold text-gray-300 mb-1">
          {title}
        </h3>
        <p className="text-gray-600">{description}</p>
      </div>

      <div className="relative w-2/12 flex justify-center">
        <div className="h-full w-1 bg-secondary absolute"></div>
        <div className="bg-gold  rounded-full p-3 z-10">{icon}</div>
      </div>

      <div className="w-5/12"></div>
    </motion.div>
  );
};

const PartnershipCard = ({ icon, title, description }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      variants={scrollVariantsRight}
      viewport={{ amount: 0.3 }}
      className="bg-slate-700/20 rounded-xl shadow-sm p-4 lg:p-8 border-l-4 border-secondary transition-all duration-300 hover:shadow-md"
    >
      <div className="flex items-start">
        <div className="shrink-0 mr-4">{icon}</div>
        <div>
          <h3 className="text-lg md:text-xl font-semibold text-gray-300 mb-0 lg:mb-2">
            {title}
          </h3>
          <p className="text-gray-500 text-sm md:text-base">{description}</p>
        </div>
      </div>
    </motion.div>
  );
};

const MergersAcquisitions = () => {
  const navigate = useNavigate();

  const partnershipPoints = [
    {
      icon: <Users className="w-6 h-6 lg:h-8 lg:w-8 text-gold" />,
      title: "Deep Industry Knowledge",
      description:
        " Decades of experience across finance, real estate, and consulting sectors",
    },
    {
      icon: <Clock className="w-6 h-6 lg:h-8 lg:w-8 text-gold" />,
      title: "Global Network",
      description:
        "Access to Centres of Influence, including bankers, lawyers, and industry leaders, for seamless deal execution.",
    },
    {
      icon: <BadgeCheck className="w-6 h-6 lg:h-8 lg:w-8 text-gold" />,
      title: "Tailored Strategies",
      description:
        "Custom M&A solutions designed to meet your business objectives",
    },
    {
      icon: <Headphones className="w-6 h-6 lg:h-8 lg:w-8 text-gold" />,
      title: "Seamless Integration",
      description:
        "Post-deal support to ensure operational alignment and growth",
    },
    {
      icon: <HandHeart className="w-6 h-6 lg:h-8 lg:w-8 text-gold" />,
      title: "Proven Results",
      description:
        "Track record of unlocking value and accelerating business transformation",
    },
  ];

  const timelineData = [
    {
      year: "2010",
      title: "Opportunity Identification",
      description:
        "Analyze market trends and identify high-value opportunities aligned with your goals.",
      icon: <Calendar className="h-6 w-6 text-slate-900" />,
      position: "left",
    },
    {
      year: "2013",
      title: "Due Diligence",
      description:
        "Perform thorough financial, operational, and market assessments to mitigate risks.",
      icon: <Award className="h-6 w-6 text-slate-900" />,
      position: "right",
    },
    {
      year: "2015",
      title: "‍Deal Structuring",
      description:
        "Develop optimal deal structures to maximize value for all stakeholders.",
      icon: <Zap className="h-6 w-6 text-slate-900" />,
      position: "left",
    },
    {
      year: "2018",
      title: "‍Negotiation & Execution",
      description:
        "Leverage our global network and expertise to finalize agreements efficiently.",
      icon: <Globe className="h-6 w-6 text-slate-900" />,
      position: "right",
    },
    {
      year: "2020",
      title: "Integration Support",
      description:
        "Ensure smooth transition and alignment of operations post-deal.",
      icon: <Rocket className="h-6 w-6 text-slate-900" />,
      position: "left",
    },
  ];

  return (
    <>
      <motion.div
        initial={{ opacity: 0.0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          delay: 0.3,
          duration: 0.8,
          ease: "easeInOut",
        }}
        className="relative flex flex-col py-10 sm:py-16 md:pt-24 lg:pt-36 lg:pb-24"
      >
        <section
          className="relative h-[62vh] md:h-[80vh] container w-full bg-gradient-to-r from-gold/5 via-secondary/10 to-gold/20 flex items-center py-20 bg-cover bg-center rounded-2xl"
          style={{ backgroundImage: "url('/images/ourExpertise/1.jpg')" }}
        >
          {/* Backdrop Overlay */}
          <div className="absolute inset-0 bg-black/80 z-0" />

          <div className="container mx-auto px-4 z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white !leading-tight mb-6">
                Strategic Mergers & Acquisitions That Drive{" "}
                <span className="text-secondary">Growth</span>
              </h1>

              <p className="text-base md:text-xl text-gray-200 mb-8 leading-relaxed">
                We optimize your logistics network and digital navigation
                systems with intelligent routing algorithms and cutting-edge
                technology.
              </p>

              <div className="inline-block">
                <Link
                  to="/contact"
                  className="flex justify-center gap-2 items-center mx-auto shadow-xl text-base bg-gradient text-slate-900 backdrop-blur-md lg:font-semibold isolation-auto border-gray-50 before:absolute before:w-full before:transition-all before:duration-700 before:hover:w-full before:-left-full before:hover:left-0 before:rounded-full before:bg-gold hover:text-slate-900 hover:border-gold before:-z-10 before:aspect-square before:hover:scale-150 before:hover:duration-700 relative z-10 ps-4 pe-1 py-1 overflow-hidden border-2 rounded-full group"
                >
                  Get Started
                  <svg
                    className="w-8 h-8 justify-end group-hover:rotate-90 group-hover:bg-gray-50 ease-linear duration-300  bg-black text-white p-2 rounded-full group-hover:border-none rotate-45"
                    viewBox="0 0 16 19"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 18C7 18.5523 7.44772 19 8 19C8.55228 19 9 18.5523 9 18H7ZM8.70711 0.292893C8.31658 -0.0976311 7.68342 -0.0976311 7.29289 0.292893L0.928932 6.65685C0.538408 7.04738 0.538408 7.68054 0.928932 8.07107C1.31946 8.46159 1.95262 8.46159 2.34315 8.07107L8 2.41421L13.6569 8.07107C14.0474 8.46159 14.6805 8.46159 15.0711 8.07107C15.4616 7.68054 15.4616 7.04738 15.0711 6.65685L8.70711 0.292893ZM9 18L9 1H7L7 18H9Z"
                      className="fill-white group-hover:fill-gray-800"
                    />
                  </svg>
                </Link>
              </div>
              {/* <div className="flex flex-row justify-center gap-4"> */}
              {/* <button
                  className="px-4 py-2 md:px-6 md:py-3 text-nowrap bg-gold hover:bg-opacity-75 text-black font-medium rounded-lg transition-colors duration-300 flex items-center justify-center"
                  onClick={() => navigate("/contact")}
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button> */}
              {/* <button
                  className="px-4 py-2 md:px-6 md:py-3 text-nowrap bg-white border border-white text-slate-900 hover:bg-opacity-80 font-medium rounded-lg transition-colors duration-300"
                  onClick={() => navigate("/contact")}
                >
                  Learn More
                </button> */}
              {/* </div> */}
            </div>
          </div>
        </section>

        {/* partner */}
        <section className="py-10 ">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <StaggeredText
                text=" Why Partner With Us"
                className="text-2xl md:text-4xl font-bold mb-4"
              />

              {/* <h2
                initial="hidden"
                whileInView="visible"
                variants={upAndDown}
                viewport={{ amount: 0.3 }}
                className="text-3xl md:text-4xl font-bold mb-4"
              >
                Why Partner With Us
              </h2> */}
              <p className="text-base md:text-lg text-gray-300">
                Our approach to M&A combines strategic foresight, market
                expertise, and a commitment to excellence. Here’s why businesses
                trust us:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {partnershipPoints.map((point, index) => (
                <PartnershipCard
                  key={index}
                  icon={point.icon}
                  title={point.title}
                  description={point.description}
                />
              ))}
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={scrollVariantsRight}
              viewport={{ amount: 0.3 }}
              className="mt-16 text-center justify-center flex"
            >
              <div className="flex w-fit flex-col md:flex-row items-center justify-center gap-2 bg-gradient-to-r from-gold to-secondary px-8 lg:px-8 py-1 lg:py-2 rounded-full shadow-lg">
                <span className="text-sm lg:text-lg font-medium text-slate-800">
                  Join 500+ satisfied clients worldwide
                </span>
                <div className="px-3 py-1  max-w-fit bg-black text-gold rounded-full font-medium text-sm">
                  98% Satisfaction Rate
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* our journey */}
        <section className="lg:pt-16 lg:pb-4 ">
          <div className="container mx-auto px-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={upAndDown}
              viewport={{ amount: 0.3 }}
              className="max-w-3xl mx-auto text-center mb-16"
            >
              <h2 className="text-2xl md:text-4xl font-bold text-gray-300 mb-4">
                Your Journey with Scan From Strategy to Success
              </h2>
              <p className="text-lg text-gray-500">
                From a small startup to an industry leader — our evolution in
                routing excellence.
              </p>
            </motion.div>

            <div className="max-w-5xl mx-auto relative">
              {timelineData.map((item, index) => (
                <TimelineItem
                  key={index}
                  year={item.year}
                  title={item.title}
                  description={item.description}
                  icon={item.icon}
                  position={item.position}
                />
              ))}
              <div className="absolute top-0 left-0 w-full h-full translate-x-1/2 pointer-events-none opacity-35 overflow-hidden">
                <div className="h-full w-1 bg-gradient-to-b from-secondary to-gold animate-slideDown hidden md:block absolute rounded-lg"></div>
              </div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default MergersAcquisitions;
